// Branded, print-ready document helpers (payslips, tax certificates, reports).
// Opens a new tab with inline-styled HTML and triggers the print dialog so the
// user can "Save as PDF" — no PDF library needed.

// Header accent follows the active brand (Augment magenta / DIH petrol).
const _hdr = (() => {
  try {
    if (JSON.parse(localStorage.getItem('augment-hr.settings'))?.brand === 'dih')
      return ['#005677', '#004864'];
  } catch { /* default below */ }
  return ['#DC0069', '#9A0048'];
})();

export const PAYDOC_CSS = `
  *{font-family:Inter,Arial,sans-serif;box-sizing:border-box}
  body{margin:0;color:#111927;background:#fff}
  .wrap{max-width:720px;margin:0 auto;padding:40px}
  .hdr{background:linear-gradient(120deg,${_hdr[0]},${_hdr[1]});color:#fff;padding:24px 28px;border-radius:14px}
  .hdr h1{margin:0;font-size:22px;letter-spacing:-0.01em}.hdr p{margin:5px 0 0;opacity:.9;font-size:13px}
  h2{font-size:14px;text-transform:uppercase;letter-spacing:.05em;color:#6C737F;margin:26px 0 8px}
  table{width:100%;border-collapse:collapse;font-size:14px}
  td{padding:8px 0;border-bottom:1px solid #F2F4F7}
  td.k{color:#6C737F}td.v{text-align:right;font-weight:600;color:#111927}
  tr.tot td{border-top:2px solid #111927;border-bottom:none;font-weight:700;padding-top:10px}
  .note{margin-top:28px;padding:14px 16px;background:#FCE8F0;border:1px solid #F7C9DD;border-radius:10px;color:#9A0048;font-size:13px;line-height:1.5}
  .foot{margin-top:22px;color:#9DA4AE;font-size:12px;text-align:center}
  @media print{.wrap{padding:18px}}
`;

export function printPayDoc(title, bodyHtml, note = '<b>System-generated payslip.</b> This document is computer-generated and does not require any signature or verification.') {
  const w = window.open('', '_blank', 'width=820,height=1040');
  if (!w) return;
  w.document.write(
    `<html><head><title>${title}</title><style>${PAYDOC_CSS}</style></head><body><div class="wrap">${bodyHtml}` +
      `<div class="note">${note}</div>` +
      `<div class="foot">Generated ${new Date().toLocaleString()} · Augment HR</div></div>` +
      `<script>window.onload=function(){window.print();}</script></body></html>`,
  );
  w.document.close();
}

export const docRow = (k, v) => `<tr><td class="k">${k}</td><td class="v">${v}</td></tr>`;

// Branded course-completion certificate. `c` = { certNo, employeeName,
// courseTitle, provider, category, durationHours, score, completedAt }.
export function printCertificate(c) {
  const w = window.open('', '_blank', 'width=900,height=680');
  if (!w) return;
  const date = c.completedAt ? new Date(c.completedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const meta = [c.provider, c.category, c.durationHours ? `${c.durationHours}h` : null, c.score != null ? `Score ${c.score}%` : null].filter(Boolean).join(' · ');
  w.document.write(
    `<html><head><title>Certificate · ${c.courseTitle || ''}</title><style>
      *{font-family:Inter,Arial,sans-serif;box-sizing:border-box}
      body{margin:0;background:#fff;color:#111927}
      .cert{max-width:840px;margin:0 auto;padding:48px}
      .frame{border:3px solid ${_hdr[0]};border-radius:18px;padding:48px 56px;text-align:center;position:relative}
      .frame::after{content:'';position:absolute;inset:10px;border:1px solid #E5E7EB;border-radius:12px;pointer-events:none}
      .eyebrow{text-transform:uppercase;letter-spacing:.22em;color:${_hdr[0]};font-size:13px;font-weight:700}
      h1{font-size:30px;margin:14px 0 6px;letter-spacing:-0.01em}
      .sub{color:#6C737F;font-size:14px;margin-bottom:28px}
      .name{font-size:34px;font-weight:800;margin:6px 0 4px}
      .rule{width:240px;height:1px;background:#E5E7EB;margin:8px auto 22px}
      .course{font-size:20px;font-weight:700;color:#111927}
      .meta{color:#6C737F;font-size:13px;margin-top:6px}
      .foot{display:flex;justify-content:space-between;align-items:flex-end;margin-top:40px;font-size:12px;color:#6C737F}
      .foot b{display:block;color:#111927;font-size:13px}
      @media print{.cert{padding:18px}}
    </style></head><body><div class="cert"><div class="frame">
      <div class="eyebrow">Certificate of Completion</div>
      <h1>Augment HR Academy</h1>
      <div class="sub">This is proudly presented to</div>
      <div class="name">${c.employeeName || ''}</div>
      <div class="rule"></div>
      <div class="sub">for successfully completing</div>
      <div class="course">${c.courseTitle || ''}</div>
      <div class="meta">${meta}</div>
      <div class="foot"><div>Certificate No.<b>${c.certNo || ''}</b></div><div style="text-align:right">Completed<b>${date}</b></div></div>
    </div></div><script>window.onload=function(){window.print();}</script></body></html>`,
  );
  w.document.close();
}

// Branded multi-column report PDF (first column left, rest right-aligned).
export function printReport(title, subtitle, headers, rows, totals) {
  const th = (t, i) => `<td style="padding:9px 0 9px 14px;font-weight:600;color:#6C737F;${i ? 'text-align:right' : ''}">${t}</td>`;
  const td = (t, i) => `<td style="padding:8px 0 8px 14px;color:#111927;${i ? 'text-align:right' : ''}">${t}</td>`;
  const head = `<tr>${headers.map((h, i) => th(h, i)).join('')}</tr>`;
  const body = rows.map((r) => `<tr>${r.map((c, i) => td(c, i)).join('')}</tr>`).join('');
  const tot = totals ? `<tr class="tot">${totals.map((c, i) => `<td class="${i ? 'v' : 'k'}" style="${i ? '' : 'padding-left:14px'}">${c}</td>`).join('')}</tr>` : '';
  printPayDoc(title, `<div class="hdr"><h1>Augment HR</h1><p>${subtitle}</p></div><h2>${title}</h2><table>${head}${body}${tot}</table>`, 'This document is system-generated by Augment HR and does not require any signature or verification.');
}
